import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, Page, Spinner, Banner } from '@shopify/polaris';
import { useAuth } from '../hooks/useAuth';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check if we have OAuth parameters (from Shopify callback)
        const shop = searchParams.get('shop');
        const code = searchParams.get('code');
        const hmac = searchParams.get('hmac');
        const state = searchParams.get('state');

        if (shop && code && hmac && state) {
          // This is a Shopify OAuth callback, redirect to backend
          const backendUrl = import.meta.env.VITE_API_URL || 'https://app.translationflow.microdesk.space';
          const callbackUrl = `${backendUrl}/api/auth/oauth/callback?${searchParams.toString()}`;
          
          window.location.href = callbackUrl;
          return;
        }

        // Check if we have backend response data
        const token = searchParams.get('token');
        const userData = searchParams.get('user');
        const accessToken = searchParams.get('accessToken');

        if (token && userData && accessToken) {
          // This is a response from our backend OAuth callback
          const user = JSON.parse(decodeURIComponent(userData));
          
          // Store authentication data directly since we have the token
          localStorage.setItem('authToken', token);
          localStorage.setItem('auth_token', token);
          localStorage.setItem('userData', userData);
          localStorage.setItem('storeData', JSON.stringify({
            shopifyDomain: user.shop,
            shopifyShopName: user.shop,
            planType: 'starter',
            status: 'active',
            isActive: true
          }));

          // Redirect to dashboard
          navigate('/dashboard', { replace: true });
          return;
        }

        // Check for error parameters
        const errorMsg = searchParams.get('error');
        if (errorMsg) {
          setError(decodeURIComponent(errorMsg));
          setIsProcessing(false);
          return;
        }

        // No valid parameters found
        setError('Invalid authentication callback');
        setIsProcessing(false);

      } catch (err) {
        console.error('Authentication error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate, login, location.state]);

  if (isProcessing) {
    return (
      <Page>
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spinner size="large" />
            <p style={{ marginTop: '1rem' }}>Completing authentication...</p>
          </div>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Banner tone="critical" title="Authentication Error">
          <p>{error}</p>
          <p>
            <a href="/login">Return to login</a>
          </p>
        </Banner>
      </Page>
    );
  }

  return null;
};

export default AuthCallback; 